<template>
  <div class="page-content almacen-page">
    <div class="almacen-wrapper">
      <div class="almacen-card">
        <!-- Header -->
        <header class="entrada-header">
          <div>
            <p class="almacen-title">Órdenes de entrega</p>
            <p class="almacen-subtitle">
              Gestiona las órdenes activas y consulta el historial de entregas.
            </p>
          </div>

          <div class="entrada-header__actions">
            <button
              type="button"
              class="ios-primary-button"
              @click="nuevaOrden"
            >
              + Nueva orden de entrega
            </button>
          </div>
        </header>

        <!-- Filtros -->
        <section class="filtros-section">
          <div class="filtro-pills">
            <button
              v-for="f in filtros"
              :key="f.value"
              type="button"
              class="filtro-pill"
              :class="{ 'filtro-pill--active': filtroEstado === f.value }"
              @click="cambiarFiltro(f.value)"
            >
              {{ f.label }}
            </button>
          </div>
        </section>

        <!-- Tabla de órdenes -->
        <section>
          <!-- LOADING -->
          <div v-if="cargando" class="estado-centrado">
            <div class="spinner"></div>
            <p>Cargando órdenes de entrega…</p>
          </div>

          <!-- VACÍO -->
          <div v-else-if="!ordenes.length" class="entrada-hint">
            No hay órdenes de entrega para el filtro seleccionado.
          </div>

          <!-- TABLA -->
          <div v-else class="tabla-wrapper">
            <table class="tabla-ordenes">
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Centro</th>
                  <th>Fecha programada</th>
                  <th>Contacto</th>
                  <th>Unidades</th>
                  <th>Estatus</th>
                  <th class="col-acciones">Acciones</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="orden in ordenes"
                  :key="orden.id_orden"
                >
                  <td>
                    <span class="tabla-folio">
                      {{ formatoFolio(orden.folio, orden.version || 1) }}
                    </span>
                    <span class="tabla-token">
                      {{ orden.token_orden }}
                    </span>
                  </td>
                  <td>
                    <div class="tabla-centro">
                      <div class="tabla-centro-nombre">
                        {{ orden.centro_nombre || '—' }}
                      </div>
                    </div>
                  </td>
                  <td>
                    {{ formatearFecha(orden.fecha_programada) }}
                  </td>
                  <td>
                    <div class="tabla-contacto">
                      <div>{{ orden.contacto_nombre || '—' }}</div>
                      <div v-if="orden.contacto_telefono" class="tabla-contacto-extra">
                        {{ orden.contacto_telefono }}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="tabla-unidades">
                      <span>
                        Total: <strong>{{ orden.total_unidades || 0 }}</strong>
                      </span>
                      <span v-if="orden.entregadas">
                        Entregadas: <strong>{{ orden.entregadas }}</strong>
                      </span>
                      <span v-if="orden.devueltas">
                        Devueltas: <strong>{{ orden.devueltas }}</strong>
                      </span>
                    </div>
                  </td>
                  <td>
                    <span
                      class="status-pill"
                      :class="'status-pill--' + (orden.status || 'pendiente')"
                    >
                      {{ etiquetaStatus(orden.status) }}
                    </span>
                  </td>
                  <td class="col-acciones">
                    <div class="acciones-stack">
                      <!-- Botón de cambio de estado -->
                      <button
                        v-if="orden.status === 'pendiente'"
                        type="button"
                        class="ios-ghost-button tabla-btn acciones-btn--small"
                        @click="cambiarEstado(orden, 'en_proceso')"
                      >
                        ▶ Poner en marcha
                      </button>

                      <button
                        v-if="orden && orden.status === 'pendiente'"
                        type="button"
                        class="ios-ghost-button"
                        @click="irAEditar(orden.id_orden)"
                      >
                        ✏ Editar orden
                      </button>

                      <button
                        v-else-if="orden.status === 'en_proceso'"
                        type="button"
                        class="ios-ghost-button tabla-btn acciones-btn--small"
                        @click="cambiarEstado(orden, 'pendiente')"
                      >
                        ⏸ Volver a pendiente
                      </button>

                      <button
                        v-if="orden.status !== 'finalizada'"
                        type="button"
                        class="ios-ghost-button tabla-btn acciones-btn--small"
                        @click="verProductosAlmacen(orden.id_orden)"
                      >
                        📦 Listar productos almacén
                      </button>

                      <!-- Ver / Imprimir -->
                      <button
                        type="button"
                        class="ios-ghost-button tabla-btn"
                        @click="verOrden(orden.id_orden)"
                      >
                        👁 Ver / Imprimir
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'DashboardOrdenesEntrega',
  data() {
    return {
      ordenes: [],
      cargando: false,
      filtroEstado: 'activas',
      filtros: [
        { value: 'activas', label: 'Activas' },
        { value: 'pendiente', label: 'Pendientes' },
        { value: 'en_proceso', label: 'En proceso' },
        { value: 'solicitando_ajuste', label: 'Solicitando ajuste' },
        { value: 'finalizada', label: 'Finalizadas' },
        { value: 'todas', label: 'Todas' },
      ],
    };
  },
  mounted() {
    this.cargarOrdenes();
  },
  computed: {
    userToken() {
      return this.$store.state.user.token;
    },
  },
  methods: {
    irAEditar($id = null) {
      if (!$id) return;
      this.$router.push({
        name: 'DashboardEditarOrdenEntrega',
        params: { id: $id },
      });
    },
    async cargarOrdenes() {
      this.cargando = true;
      try {
        const res = await axios.get('/api/almacen/ordenes-entrega/listar', {
          params: { filtro_estado: this.filtroEstado },
        });

        if (res.data?.status === 'ok') {
          this.ordenes = Array.isArray(res.data.ordenes) ? res.data.ordenes : [];
        } else {
          this.ordenes = [];
        }
      } catch (error) {
        console.error('Error al cargar órdenes de entrega:', error);
        this.$toast?.error('No se pudieron cargar las órdenes de entrega.');
        this.ordenes = [];
      } finally {
        this.cargando = false;
      }
    },
    cambiarFiltro(valor) {
      if (this.filtroEstado === valor) return;
      this.filtroEstado = valor;
      this.cargarOrdenes();
    },
    formatoFolio(folio, version = 1) {
      if (!folio && folio !== 0) return 'SIN FOLIO';
      const n = parseInt(folio, 10);
      if (Number.isNaN(n)) return folio;

      const base = 'ENT-' + String(n).padStart(4, '0');
      if (!version || version <= 1) {
        return base;
      }
      return `${base}-${version}`;
    },

    formatearFecha(f) {
      if (!f) return '—';
      const [y, m, d] = f.split('-');
      if (!y || !m || !d) return f;
      return `${d}/${m}/${y}`;
    },
    etiquetaStatus(status) {
      switch (status) {
        case 'pendiente':
          return 'Pendiente';
        case 'en_proceso':
          return 'En proceso';
        case 'finalizada':
          return 'Finalizada';
        case 'solicitando_ajuste':
          return 'Solicitando ajuste';
        default:
          return status || 'Pendiente';
      }
    },
    verOrden(id) {
      this.$router.push({
        name: 'VerOrdenEntrega',
        params: { id },
      });
    },
    verProductosAlmacen(id) {
      this.$router.push({
        name: 'ListaProductosAlmacenEntrega',
        params: { id },
      });
    },
    nuevaOrden() {
      this.$router.push({ name: 'DashboardCrearOrdenEntrega' });
    },
    async cambiarEstado(orden, nuevoEstado) {
      if (!orden || !orden.id_orden) return;

      try {
        const res = await axios.post(
          '/api/almacen/ordenes-entrega/cambiar-estado',
          {
            id_orden: orden.id_orden,
            status: nuevoEstado,
          }
        );

        if (res.data?.status === 'ok') {
          this.$toast?.success(
            `Orden actualizada a "${this.etiquetaStatus(nuevoEstado)}".`
          );
          this.cargarOrdenes();
        } else {
          this.$toast?.error(
            res.data?.message || 'No se pudo actualizar el estado de la orden.'
          );
        }
      } catch (error) {
        console.error('Error al cambiar estado de orden:', error);
        this.$toast?.error('Error al cambiar el estado de la orden.');
      }
    },
  },
};
</script>

<style scoped>
.almacen-page {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 1.5rem 1rem;
}

.almacen-wrapper {
  width: 100%;
  max-width: 1100px;
}

.almacen-card {
  border-radius: 1.8rem;
  padding: 1.5rem 1.4rem 1.2rem;
  background: radial-gradient(
      circle at 0 0,
      rgba(148, 163, 184, 0.35),
      rgba(15, 23, 42, 0.96)
    );
  backdrop-filter: blur(24px);
  border: 1px solid rgba(148, 163, 184, 0.6);
  box-shadow:
    0 18px 45px rgba(15, 23, 42, 0.9),
    0 0 0 1px rgba(15, 23, 42, 0.85);
}

.almacen-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #e5e7eb;
}

.almacen-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
}

.entrada-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
}

.entrada-header__actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

/* Filtros */
.filtros-section {
  margin-bottom: 0.7rem;
}

.filtro-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.filtro-pill {
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.8);
  background: rgba(15, 23, 42, 0.9);
  color: #e5e7eb;
  font-size: 0.78rem;
  padding: 0.18rem 0.75rem;
  cursor: pointer;
}

.filtro-pill--active {
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 0.7),
    rgba(37, 99, 235, 1)
  );
  border-color: rgba(129, 140, 248, 0.9);
}

/* Tabla */
.tabla-wrapper {
  border-radius: 1.1rem;
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: rgba(15, 23, 42, 0.96);
  padding: 0.4rem 0.5rem;
  overflow-x: auto;
}

.tabla-ordenes {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
  color: #e5e7eb;
}

.tabla-ordenes th,
.tabla-ordenes td {
  padding: 0.35rem 0.4rem;
  border-bottom: 1px solid rgba(30, 64, 175, 0.5);
  text-align: left;
}

.tabla-ordenes thead th {
  font-weight: 600;
  color: #cbd5f5;
  border-bottom: 1px solid rgba(148, 163, 184, 0.7);
}

.tabla-ordenes tbody tr:last-child td {
  border-bottom: none;
}

.col-acciones {
  width: 210px;
}

/* Acciones en columna */
.acciones-stack {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.acciones-btn--small {
  font-size: 0.72rem;
  padding-block: 0.3rem;
}

/* Celda folio */
.tabla-folio {
  display: block;
  font-weight: 600;
}

.tabla-token {
  display: block;
  font-size: 0.68rem;
  color: #94a3b8;
}

/* Centro */
.tabla-centro-nombre {
  font-weight: 500;
}

/* Contacto */
.tabla-contacto-extra {
  font-size: 0.7rem;
  color: #94a3b8;
}

/* Unidades */
.tabla-unidades {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
}

/* Status */
.status-pill {
  border-radius: 999px;
  padding: 0.1rem 0.6rem;
  font-size: 0.7rem;
  border: 1px solid rgba(148, 163, 184, 0.7);
  background: rgba(15, 23, 42, 0.96);
}

.status-pill--pendiente {
  border-color: rgba(234, 179, 8, 0.9);
}

.status-pill--en_proceso {
  border-color: rgba(59, 130, 246, 0.9);
}

.status-pill--finalizada {
  border-color: rgba(34, 197, 94, 0.9);
}

.status-pill--solicitando_ajuste {
  border-color: rgba(244, 114, 182, 0.9);
}

/* Botones iOS */
.ios-primary-button,
.ios-ghost-button {
  border-radius: 999px;
  padding: 0.45rem 1rem;
  font-size: 0.8rem;
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    background 0.16s ease,
    border-color 0.16s ease,
    transform 0.08s ease,
    box-shadow 0.16s ease;
}

.ios-primary-button {
  background: radial-gradient(
    circle at 0 0,
    rgba(129, 140, 248, 0.7),
    rgba(37, 99, 235, 1)
  );
  border-color: rgba(129, 140, 248, 0.9);
  color: #e5e7eb;
  box-shadow: 0 14px 32px rgba(37, 99, 235, 0.7);
}

.ios-primary-button:hover {
  transform: translateY(-0.5px);
  box-shadow: 0 18px 38px rgba(37, 99, 235, 0.9);
}

.ios-ghost-button {
  background: rgba(15, 23, 42, 0.9);
  border-color: rgba(148, 163, 184, 0.7);
  color: #e5e7eb;
}

.ios-ghost-button:hover {
  background: rgba(15, 23, 42, 1);
}

.tabla-btn {
  padding-inline: 0.7rem;
}

/* Hints */
.entrada-hint {
  font-size: 0.8rem;
  color: #94a3b8;
}

/* ESTADOS CENTRADOS + SPINNER (igual que recolección) */
.estado-centrado {
  min-height: 220px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
  justify-content: center;
  color: #a5b0de;
  font-size: 0.9rem;
}

.spinner {
  width: 26px;
  height: 26px;
  border-radius: 999px;
  border: 3px solid rgba(115, 145, 255, 0.25);
  border-top-color: rgba(132, 165, 255, 0.95);
  animation: spin 0.9s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .entrada-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .col-acciones {
    width: auto;
  }

  .acciones-stack {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.3rem;
  }
}
</style>
